/**
 * On-device store, in the wishful-kit shape (plan §7). Identity fields the kit
 * owns are kept; everything Can Do owns sits under `apps['can-do']`; any other
 * app's key is passed through untouched.
 */
import { normalizeAppState, EMPTY_APP_STATE, eventsFor } from '../domain/skills.js';

export const APP_ID = 'can-do';
export const STORAGE_KEY = 'can-do-v1';
export const MAX_PROFILES = 6;
export const PROFILE_COLORS = ['teal', 'green', 'orange', 'blue', 'yellow', 'pink'];

let counter = 0;
export function nextId(prefix) {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${counter.toString(36)}`;
}

/** Local calendar date. A family agrees on what day it is. */
export function today(d = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export const EMPTY_STORE = Object.freeze({
  version: 1,
  activeId: null,
  parentPin: null,
  profiles: [],
});

function normalizeProfile(raw, i) {
  const p = raw && typeof raw === 'object' ? raw : {};
  const apps = p.apps && typeof p.apps === 'object' ? { ...p.apps } : {};
  apps[APP_ID] = normalizeAppState(apps[APP_ID]);
  return {
    id: typeof p.id === 'string' ? p.id : nextId('kid'),
    name: typeof p.name === 'string' ? p.name : `Kid ${i + 1}`,
    color: PROFILE_COLORS.includes(p.color) ? p.color : PROFILE_COLORS[i % PROFILE_COLORS.length],
    avatar: typeof p.avatar === 'string' ? p.avatar : null,
    createdAt: typeof p.createdAt === 'number' ? p.createdAt : Date.now(),
    birthMonth: Number.isInteger(p.birthMonth) ? p.birthMonth : 1,
    birthYear: Number.isInteger(p.birthYear) ? p.birthYear : 2016,
    wallet: p.wallet && typeof p.wallet === 'object' ? p.wallet : {},
    apps,
  };
}

export function normalizeStore(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const profiles = (Array.isArray(r.profiles) ? r.profiles : []).slice(0, MAX_PROFILES).map(normalizeProfile);
  const activeId = profiles.some((p) => p.id === r.activeId) ? r.activeId : (profiles[0]?.id ?? null);
  return {
    version: 1,
    activeId,
    parentPin: typeof r.parentPin === 'string' && /^\d{4}$/.test(r.parentPin) ? r.parentPin : null,
    profiles,
  };
}

export function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return normalizeStore(raw ? JSON.parse(raw) : EMPTY_STORE);
  } catch {
    return normalizeStore(EMPTY_STORE);
  }
}

export function save(store) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(store)); } catch { /* private mode */ }
}

// ── pure mutations: (store, ...) → new store ─────────────────────────────

export function addProfile(store, { name, birthMonth, birthYear, enabled = [], wishes = [] }) {
  if (store.profiles.length >= MAX_PROFILES) return store;
  const i = store.profiles.length;
  const profile = normalizeProfile({
    id: nextId('kid'), name, birthMonth, birthYear,
    color: PROFILE_COLORS[i % PROFILE_COLORS.length],
    apps: { [APP_ID]: { ...EMPTY_APP_STATE, enabled, wishes } },
  }, i);
  return { ...store, activeId: store.activeId ?? profile.id, profiles: [...store.profiles, profile] };
}

export function setActive(store, id) {
  return store.profiles.some((p) => p.id === id) ? { ...store, activeId: id } : store;
}

export function setPin(store, pin) {
  return { ...store, parentPin: /^\d{4}$/.test(pin ?? '') ? pin : null };
}

export function updateApp(store, profileId, fn) {
  return {
    ...store,
    profiles: store.profiles.map((p) => (p.id !== profileId ? p : {
      ...p, apps: { ...p.apps, [APP_ID]: fn(p.apps[APP_ID]) },
    })),
  };
}

export function appendEvent(store, profileId, event) {
  const full = { id: nextId('ev'), at: today(), by: 'kid', ...event };
  return updateApp(store, profileId, (app) => ({ ...app, events: [...app.events, full] }));
}

/**
 * Opening a skill's steps is the first rung. Idempotent per skill (after the
 * last parent reset), so a re-render or a second visit never logs it twice.
 */
export function introduceSkill(store, profileId, skillId) {
  const profile = store.profiles.find((p) => p.id === profileId);
  if (!profile) return store;
  if (eventsFor(appOf(profile).events, skillId).length > 0) return store;
  return appendEvent(store, profileId, { skillId, type: 'introduced' });
}

/** A backup is the whole store plus a stamp. Import is normalize, nothing more. */
export function exportStore(store, at = today()) {
  return { app: APP_ID, exportedAt: at, store };
}

export function importStore(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const inner = r.store && typeof r.store === 'object' ? r.store : r;
  const store = normalizeStore(inner);
  if (store.profiles.length === 0) throw new Error('That file has no kids in it.');
  return store;
}

/** Add several skills to a child's list in one commit. Never removes. */
export function enableMany(store, profileId, skillIds) {
  return updateApp(store, profileId, (app) => ({ ...app, enabled: [...new Set([...app.enabled, ...skillIds])] }));
}

export function toggleEnabled(store, profileId, skillId, on) {
  return updateApp(store, profileId, (app) => {
    const set = new Set(app.enabled);
    if (on) set.add(skillId); else set.delete(skillId);
    return { ...app, enabled: [...set] };
  });
}

export function toggleAdultEnabled(store, profileId, skillId, on) {
  return updateApp(store, profileId, (app) => {
    const set = new Set(app.adultEnabled);
    if (on) set.add(skillId); else set.delete(skillId);
    return { ...app, adultEnabled: [...set] };
  });
}

export function setPrivilege(store, profileId, skillId, text) {
  return updateApp(store, profileId, (app) => {
    const privileges = { ...app.privileges };
    if (text && text.trim()) privileges[skillId] = text.trim(); else delete privileges[skillId];
    return { ...app, privileges };
  });
}

export function renameProfile(store, profileId, fields) {
  return { ...store, profiles: store.profiles.map((p) => (p.id === profileId ? normalizeProfile({ ...p, ...fields }, 0) : p)) };
}

export function removeProfile(store, profileId) {
  const profiles = store.profiles.filter((p) => p.id !== profileId);
  return normalizeStore({ ...store, profiles });
}

export const appOf = (profile) => profile.apps[APP_ID];
